// Google Fonts Service for dynamic font loading and management

export interface GoogleFont {
  family: string;
  variants: string[];
  subsets: string[];
  category: 'serif' | 'sans-serif' | 'display' | 'handwriting' | 'monospace';
  files: Record<string, string>;
}

export interface GoogleFontsResponse {
  items: GoogleFont[];
}

class GoogleFontsService {
  private static instance: GoogleFontsService;
  private fonts: GoogleFont[] = [];
  private loadedFonts: Set<string> = new Set();
  private fontCache: Map<string, GoogleFont> = new Map();

  // Popular fonts that should be shown first
  private popularFonts = [
    'Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Oswald', 'Source Sans Pro',
    'Raleway', 'PT Sans', 'Lora', 'Nunito', 'Ubuntu', 'Playfair Display', 'Merriweather',
    'Poppins', 'Noto Sans', 'Fira Sans', 'Work Sans', 'Crimson Text', 'Libre Baskerville'
  ];

  // System fonts that are always available
  private systemFonts: GoogleFont[] = [
    {
      family: 'Arial',
      variants: ['regular', '700'],
      subsets: ['latin'],
      category: 'sans-serif',
      files: {}
    },
    {
      family: 'Helvetica',
      variants: ['regular', '700'],
      subsets: ['latin'],
      category: 'sans-serif',
      files: {}
    },
    {
      family: 'Times New Roman',
      variants: ['regular', '700'],
      subsets: ['latin'],
      category: 'serif',
      files: {}
    },
    {
      family: 'Georgia',
      variants: ['regular', '700'],
      subsets: ['latin'],
      category: 'serif',
      files: {}
    },
    {
      family: 'Courier New',
      variants: ['regular', '700'],
      subsets: ['latin'],
      category: 'monospace',
      files: {}
    }
  ];

  private constructor() {}

  public static getInstance(): GoogleFontsService {
    if (!GoogleFontsService.instance) {
      GoogleFontsService.instance = new GoogleFontsService();
    }
    return GoogleFontsService.instance;
  }

  // Initialize with popular fonts (can be called without API key)
  public async initializeWithPopularFonts(): Promise<GoogleFont[]> {
    // Return system fonts + popular Google fonts (without loading them yet)
    const popularGoogleFonts: GoogleFont[] = this.popularFonts.map(family => ({
      family,
      variants: ['regular', '700'],
      subsets: ['latin'],
      category: 'sans-serif' as const,
      files: {}
    }));

    this.fonts = [...this.systemFonts, ...popularGoogleFonts];
    return this.fonts;
  }

  // Load full font list from Google Fonts API (requires API key)
  public async loadGoogleFonts(apiKey?: string): Promise<GoogleFont[]> {
    if (this.fonts.length > 0) {
      return this.fonts;
    }

    try {
      // If no API key, use popular fonts only
      if (!apiKey) {
        return this.initializeWithPopularFonts();
      }

      const response = await fetch(
        `https://www.googleapis.com/webfonts/v1/webfonts?key=${apiKey}&sort=popularity`
      );

      if (!response.ok) {
        console.warn('Failed to load Google Fonts, using popular fonts only');
        return this.initializeWithPopularFonts();
      }

      const data: GoogleFontsResponse = await response.json();
      
      // Combine system fonts with Google fonts
      this.fonts = [...this.systemFonts, ...data.items];
      
      // Cache fonts for quick lookup
      this.fonts.forEach(font => {
        this.fontCache.set(font.family.toLowerCase(), font);
      });

      return this.fonts;
    } catch (error) {
      console.error('Error loading Google Fonts:', error);
      return this.initializeWithPopularFonts();
    }
  }

  // Get all available fonts
  public getFonts(): GoogleFont[] {
    return this.fonts;
  }

  // Get popular fonts first, then others
  public getFontsSorted(): GoogleFont[] {
    const popular = this.fonts.filter(font => 
      this.popularFonts.includes(font.family) || this.systemFonts.some(sf => sf.family === font.family)
    );
    const others = this.fonts.filter(font => 
      !this.popularFonts.includes(font.family) && !this.systemFonts.some(sf => sf.family === font.family)
    );
    
    return [...popular, ...others];
  }

  // Search fonts by name
  public searchFonts(query: string): GoogleFont[] {
    if (!query.trim()) {
      return this.getFontsSorted();
    }

    const searchTerm = query.toLowerCase();
    return this.fonts.filter(font =>
      font.family.toLowerCase().includes(searchTerm)
    );
  }

  // Get fonts by category
  public getFontsByCategory(category: GoogleFont['category']): GoogleFont[] {
    return this.fonts.filter(font => font.category === category);
  }

  // Load a specific font dynamically
  public async loadFont(fontFamily: string, variants: string[] = ['400']): Promise<void> {
    // Skip system fonts
    if (this.systemFonts.some(font => font.family === fontFamily)) {
      return;
    }

    const fontKey = `${fontFamily}:${variants.join(',')}`;
    
    if (this.loadedFonts.has(fontKey)) {
      return; // Already loaded
    }

    try {
      // Create Google Fonts URL
      const variantString = variants.map(variant => {
        // Convert weight numbers to Google Fonts format
        if (variant === '400' || variant === 'regular') return '400';
        if (variant === '700' || variant === 'bold') return '700';
        return variant;
      }).join(',');

      const fontUrl = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontFamily)}:wght@${variantString}&display=swap`;

      // Create and append link element
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = fontUrl;
      
      // Wait for font to load
      await new Promise<void>((resolve, reject) => {
        link.onload = () => resolve();
        link.onerror = () => reject(new Error(`Failed to load font: ${fontFamily}`));
        document.head.appendChild(link);
      });

      this.loadedFonts.add(fontKey);
      console.log(`Loaded font: ${fontFamily}`);
    } catch (error) {
      console.error(`Error loading font ${fontFamily}:`, error);
    }
  }

  // Check if a font is loaded
  public isFontLoaded(fontFamily: string, variants: string[] = ['400']): boolean {
    const fontKey = `${fontFamily}:${variants.join(',')}`;
    return this.loadedFonts.has(fontKey) || this.systemFonts.some(font => font.family === fontFamily);
  }

  // Get font by family name
  public getFont(fontFamily: string): GoogleFont | undefined {
    return this.fontCache.get(fontFamily.toLowerCase()) || 
           this.fonts.find(font => font.family.toLowerCase() === fontFamily.toLowerCase());
  }

  // Preload popular fonts
  public async preloadPopularFonts(): Promise<void> {
    const popularToLoad = this.popularFonts.slice(0, 10); // Load first 10 popular fonts
    
    const loadPromises = popularToLoad.map(fontFamily => 
      this.loadFont(fontFamily, ['400', '700'])
    );

    try {
      await Promise.all(loadPromises);
      console.log('Preloaded popular fonts');
    } catch (error) {
      console.error('Error preloading fonts:', error);
    }
  }

  // Get CSS font-family string with fallbacks
  public getFontFamilyCSS(fontFamily: string): string {
    const font = this.getFont(fontFamily);
    if (!font) return fontFamily;

    const fallbacks = {
      'serif': ', Georgia, "Times New Roman", Times, serif',
      'sans-serif': ', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      'monospace': ', "Courier New", Courier, monospace',
      'display': ', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      'handwriting': ', cursive'
    };

    return `"${fontFamily}"${fallbacks[font.category] || fallbacks['sans-serif']}`;
  }
}

export default GoogleFontsService;
